import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnnoncenewService } from '../service/annoncenew.service';
import { AnnonceFormData } from '../models/annonce.model';

@Component({
  selector: 'app-addnewannonce',
  templateUrl: './addnewannonce.component.html',
  styleUrls: ['./addnewannonce.component.scss']
})
export class AddnewannonceComponent implements OnInit {
  annonceForm: FormGroup;
  isLoading = false;
  isSubmitting = false;

  villes = [
    'Tunis', 'Ariana', 'Ben Arous', 'Manouba', 'Nabeul', 'Zaghouan',
    'Bizerte', 'Béja', 'Jendouba', 'Kef', 'Siliana', 'Sousse',
    'Monastir', 'Mahdia', 'Sfax', 'Kairouan', 'Kasserine',
    'Sidi Bouzid', 'Gabès', 'Médenine', 'Tataouine', 'Gafsa',
    'Tozeur', 'Kébili'
  ];

  typeOptions = [
    { value: 'Vente', label: 'Vente' },
    { value: 'Location', label: 'Location' },
    { value: 'Location Vacances', label: 'Location Vacances' }
  ];

  annonceTypes = [
    { value: 'Appartements', label: 'Appartements' },
    { value: 'Bureaux', label: 'Bureaux' },
    { value: 'Commerces', label: 'Commerces' },
    { value: 'Duplexes', label: 'Duplexes' },
    { value: 'Industriel', label: 'Industriel' },
    { value: 'Lots', label: 'Lots' },
    { value: 'Studios', label: 'Studios' },
    { value: 'Terrain agricole', label: 'Terrain agricole' },
    { value: 'Villas', label: 'Villas / maisons' }
  ];

  constructor(
    private fb: FormBuilder,
    private annonceService: AnnoncenewService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {
    this.annonceForm = this.fb.group({
      annonceName: ['', [Validators.required, Validators.minLength(3)]],
      adresse: ['', [Validators.required, Validators.minLength(5)]],
      ville: ['', Validators.required],
      typeAnnonce: ['', Validators.required],
      description: ['', [Validators.required, Validators.minLength(20)]],
      surface: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      prix: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      phone1: ['', [Validators.required, Validators.pattern(/^[0-9]{8}$/)]],
      phone2: ['', Validators.pattern(/^[0-9]{8}$/)],
      email: ['', [Validators.email]],
      urlGoogleMap: [''],
      urlYoutube: [''],
      photoPrincipale: ['', Validators.required],
      imagesList: ['']
    });
  }

  ngOnInit(): void {}

  get formControls() {
    return this.annonceForm.controls;
  }

  onSubmit(): void {
    if (this.annonceForm.invalid) {
      this.markFormGroupTouched(this.annonceForm);
      this.snackBar.open('Veuillez remplir tous les champs obligatoires correctement', 'Fermer', {
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSubmitting = true;
    
    const formData: AnnonceFormData = {
      annonceName: this.annonceForm.value.annonceName,
      adresse: this.annonceForm.value.adresse,
      ville: this.annonceForm.value.ville,
      typeAnnonce: this.annonceForm.value.typeAnnonce,
      description: this.annonceForm.value.description,
      surface: this.annonceForm.value.surface,
      prix: this.annonceForm.value.prix,
      phone1: this.annonceForm.value.phone1,
      phone2: this.annonceForm.value.phone2 || undefined,
      email: this.annonceForm.value.email || undefined,
      urlGoogleMap: this.annonceForm.value.urlGoogleMap || undefined,
      urlYoutube: this.annonceForm.value.urlYoutube || undefined,
      photoPrincipale: this.annonceForm.value.photoPrincipale,
      imagesList: this.annonceForm.value.imagesList || undefined
    };

    this.annonceService.createAnnonce(formData).subscribe({
      next: (response) => {
        this.snackBar.open('Annonce créée avec succès!', 'Fermer', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.router.navigate(['/annoncenew']);
      },
      error: (error) => {
        console.error('Error creating annonce:', error);
        this.snackBar.open('Erreur lors de la création de l\'annonce', 'Fermer', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
        this.isSubmitting = false;
      },
      complete: () => {
        this.isSubmitting = false;
      }
    });
  }

  onCancel(): void {
    if (this.annonceForm.dirty) {
      const confirmLeave = confirm('Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter?');
      if (confirmLeave) {
        this.router.navigate(['/annoncenew']);
      }
    } else {
      this.router.navigate(['/annoncenew']);
    }
  }

  markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  formatImagesList(event: any): void {
    const value = event.target.value;
    if (value) {
      // Split by commas and newlines, filter empty strings, join with commas
      const imagesArray = value.split(/[,\n]+/).filter((img: string) => img.trim() !== '');
      this.annonceForm.patchValue({ imagesList: imagesArray.join(',') });
    }
  }

  getImagesCount(): number {
    const imagesList = this.annonceForm.get('imagesList')?.value;
    if (!imagesList) return 0;
    return imagesList.split(',').filter((img: string) => img.trim() !== '').length;
  }

  previewImage(): string {
    const mainImage = this.annonceForm.get('photoPrincipale')?.value;
    return mainImage || 'assets/images/no-image.jpg';
  }
}